import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EMPLOYEE_ACTION } from "../../redux/action";
import { getHrmsDashboard } from "../../utils/hrmsApi";
import HrmsDashboardAdmin from "./HrmsDashboardAdmin";
import {
  filterHolidaysThisAndNextMonth,
  filterNewJoineesThisMonth,
} from "./hrmsDashboardFilters";
import {
  buildEmployeeByIdMap,
  formatAppreciationParties,
} from "./hrmsAppreciationUtils";
import "./hrmsDashboard.scss";

const emptyModel = () => ({
  meta: {},
  birthdays: [],
  newJoinees: [],
  workAnniversaries: [],
  holidays: [],
  events: [],
  announcements: [],
  appreciations: [],
});

function DashboardCard({ title, children }) {
  return (
    <div className="hrms-dash-card">
      <h3 className="hrms-dash-card__title">{title}</h3>
      <div className="hrms-dash-card__body">{children}</div>
    </div>
  );
}

function Avatar({ src, alt }) {
  if (src) {
    return <img className="hrms-dash-avatar" src={src} alt="" />;
  }
  const letter = (alt || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="hrms-dash-avatar hrms-dash-avatar--placeholder" aria-hidden>
      {letter}
    </div>
  );
}

function ListRow({ left, primary, secondary, badge }) {
  return (
    <div className="hrms-dash-row">
      {left != null ? <div className="hrms-dash-row__left">{left}</div> : null}
      <div className="hrms-dash-row__text">
        <div className="hrms-dash-row__primary">
          {primary}
          {badge ? (
            <span className="hrms-dash-badge hrms-dash-badge--warn">{badge}</span>
          ) : null}
        </div>
        {secondary ? (
          <div className="hrms-dash-row__secondary">{secondary}</div>
        ) : null}
      </div>
    </div>
  );
}

function truthyRestricted(v) {
  return v === true || v === 1 || v === "1";
}

function normalizeDashboardData(body) {
  const d = body?.data || {};
  return {
    meta: d.meta && typeof d.meta === "object" ? d.meta : {},
    birthdays: Array.isArray(d.birthdays) ? d.birthdays : [],
    newJoinees: Array.isArray(d.new_joinees) ? d.new_joinees : [],
    workAnniversaries: Array.isArray(d.work_anniversaries)
      ? d.work_anniversaries
      : [],
    holidays: Array.isArray(d.holidays) ? d.holidays : [],
    events: Array.isArray(d.events) ? d.events : [],
    announcements: Array.isArray(d.announcements) ? d.announcements : [],
    appreciations: Array.isArray(d.appreciations) ? d.appreciations : [],
  };
}

export default function HrmsDashboard({ isAdmin = false }) {
  const dispatch = useDispatch();
  const activeEmployees = useSelector(
    (state) => state.employee?.activeEmployees || [],
  );
  const [model, setModel] = useState(emptyModel);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const employeeById = useMemo(
    () => buildEmployeeByIdMap(activeEmployees),
    [activeEmployees],
  );

  useEffect(() => {
    if (!activeEmployees.length) {
      dispatch(EMPLOYEE_ACTION.getActiveEmployees());
    }
  }, [dispatch, activeEmployees.length]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getHrmsDashboard();
      const body = res?.data || {};
      if (res?.status === 200 && body.status === 1 && body.data) {
        setModel(normalizeDashboardData(body));
      } else {
        setModel(emptyModel());
        setError(body.message || body.error || "Could not load dashboard.");
      }
    } catch (e) {
      setModel(emptyModel());
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (e?.response?.status === 403
          ? "You do not have access to the HRMS dashboard."
          : null);
      setError(msg || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const newJoineesThisMonth = useMemo(
    () => filterNewJoineesThisMonth(model.newJoinees),
    [model.newJoinees],
  );

  const holidaysThisAndNextMonth = useMemo(
    () => filterHolidaysThisAndNextMonth(model.holidays),
    [model.holidays],
  );

  const emptyHint = (label) => (
    <div className="hrms-dash-empty">{loading ? "Loading…" : `No ${label}.`}</div>
  );

  const holidayRegion = model.meta?.holidayRegion ?? model.meta?.holiday_region;

  return (
    <div className="hrms-dashboard">
      <div className="hrms-dash-toolbar">
        <button
          type="button"
          className="hrms-dash-toolbar__btn"
          onClick={() => load()}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {error ? <div className="hrms-dash-banner">{error}</div> : null}

      {holidayRegion ? (
        <p className="hrms-dash-meta">
          Holiday list matches your region: <strong>{String(holidayRegion)}</strong>
          . If holidays look wrong, check your profile country / location with
          admin.
        </p>
      ) : null}

      <div className="hrms-dash-grid">
        <DashboardCard title="Birthdays this month">
          {model.birthdays.length === 0
            ? emptyHint("birthdays this month")
            : model.birthdays.map((row, i) => (
                <ListRow
                  key={row.employeeId || row.employee_id || `b-${i}`}
                  left={
                    <Avatar
                      src={row.photoUrl || row.photo_url}
                      alt={row.displayName || row.display_name}
                    />
                  }
                  primary={`${row.dayLabel || row.day_label || "-"} - ${row.displayName || row.display_name || "-"}`}
                  secondary={row.subtitle}
                />
              ))}
        </DashboardCard>

        <DashboardCard title="New joinee this month">
          {newJoineesThisMonth.length === 0
            ? emptyHint("new joinees this month")
            : newJoineesThisMonth.map((row, i) => (
                <ListRow
                  key={row.employeeId || row.employee_id || `n-${i}`}
                  left={
                    <Avatar
                      src={row.photoUrl || row.photo_url}
                      alt={row.displayName || row.display_name}
                    />
                  }
                  primary={row.displayName || row.display_name || "—"}
                  secondary={row.joinedLabel || row.joined_label}
                />
              ))}
        </DashboardCard>

        <DashboardCard title="Work anniversary">
          {model.workAnniversaries.length === 0
            ? emptyHint("work anniversaries")
            : model.workAnniversaries.map((row, i) => (
                <ListRow
                  key={row.employeeId || row.employee_id || `a-${i}`}
                  left={
                    <Avatar
                      src={row.photoUrl || row.photo_url}
                      alt={row.displayName || row.display_name}
                    />
                  }
                  primary={`${row.displayName || row.display_name || "—"} - ${row.tenureLabel || row.tenure_label || ""}`}
                  secondary={(() => {
                    const next =
                      row.nextAnniversaryDate || row.next_anniversary_date;
                    const yrs =
                      row.yearsCompleted ?? row.years_completed ?? null;
                    const bits = [];
                    if (next) bits.push(`Next: ${next}`);
                    if (yrs != null && yrs !== "")
                      bits.push(`${yrs} years completed`);
                    return bits.length ? bits.join(" · ") : undefined;
                  })()}
                />
              ))}
        </DashboardCard>

        <DashboardCard title="Holidays (this & next month)">
          {holidaysThisAndNextMonth.length === 0
            ? emptyHint("holidays in this and next month")
            : holidaysThisAndNextMonth.map((row, i) => (
                <ListRow
                  key={row.holidayId || row.holiday_id || `h-${i}`}
                  primary={`${row.dateLabel || row.date_label || row.date || "—"} · ${row.name || row.title || ""}`}
                  secondary={
                    row.locationRegion || row.location_region
                      ? String(row.locationRegion || row.location_region)
                      : undefined
                  }
                  badge={
                    truthyRestricted(row.isRestricted)
                      ? "Optional / restricted"
                      : null
                  }
                />
              ))}
        </DashboardCard>

        <DashboardCard title="Upcoming events">
          {model.events.length === 0
            ? emptyHint("events")
            : model.events.map((row, i) => (
                <ListRow
                  key={row.eventId || row.event_id || `e-${i}`}
                  primary={`${row.dateLabel || row.date_label || row.date || "—"} · ${row.title || ""}`}
                  secondary={row.location}
                />
              ))}
        </DashboardCard>

        <DashboardCard title="Announcements">
          {model.announcements.length === 0
            ? emptyHint("announcements")
            : model.announcements.map((row, i) => (
                <ListRow
                  key={row.announcementId || row.announcement_id || `an-${i}`}
                  primary={
                    row.title
                      ? `${row.title} — ${row.text || row.body || ""}`
                      : row.text || row.body || "—"
                  }
                  secondary={row.dateLabel || row.date_label}
                />
              ))}
        </DashboardCard>

        <DashboardCard title="Appreciation">
          {model.appreciations.length === 0
            ? emptyHint("appreciations")
            : model.appreciations.map((row, i) => (
                <ListRow
                  key={row.appreciationId || row.appreciation_id || `ap-${i}`}
                  primary={row.text || row.message || "—"}
                  secondary={formatAppreciationParties(row, employeeById)}
                />
              ))}
        </DashboardCard>
      </div>

      {isAdmin ? (
        <HrmsDashboardAdmin dashboardModel={model} onChanged={load} />
      ) : null}
    </div>
  );
}
